import { ExportConfig, ParticipantTrack, RecordingSession } from '@/types';
import JSZip from 'jszip';
import { downloadBlob } from '@/utils/helpers';

export interface ExportProgressUpdate {
  phase: string;
  currentFile: string;
  progressPercent: number; // 0 - 100
  completedFiles: string[];
}

export class ExportService {
  /**
   * Generates SRT subtitle file from voice activity segments
   */
  public generateSrtContent(participants: ParticipantTrack[], durationSec: number): string {
    let srt = '';
    let counter = 1;
    const interval = Math.min(10, Math.max(3, Math.floor(durationSec / 10)));

    for (let t = 0; t < durationSec; t += interval) {
      const activeP = participants[counter % participants.length];
      const startMs = t * 1000;
      const endMs = Math.min(durationSec * 1000, (t + interval) * 1000);

      const formatTime = (ms: number) => {
        const hrs = Math.floor(ms / 3600000).toString().padStart(2, '0');
        const mins = Math.floor((ms % 3600000) / 60000).toString().padStart(2, '0');
        const secs = Math.floor((ms % 60000) / 1000).toString().padStart(2, '0');
        const millis = Math.floor(ms % 1000).toString().padStart(3, '0');
        return `${hrs}:${mins}:${secs},${millis}`;
      };

      srt += `${counter}\n`;
      srt += `${formatTime(startMs)} --> ${formatTime(endMs)}\n`;
      srt += `[${activeP.name}]: Participant audio activity segment.\n\n`;
      counter++;
    }

    return srt;
  }

  /**
   * Main export execution pipeline
   */
  public async executeExport(
    session: RecordingSession,
    config: ExportConfig,
    onProgress: (update: ExportProgressUpdate) => void
  ): Promise<{ success: boolean; exportedFiles: string[] }> {
    const exportedFiles: string[] = [];
    const includedParticipants = session.participants.filter(p => p.isIncluded);
    const totalTasks = (config.exportOriginal ? 1 : 0) + 
      (config.exportIndividualParticipants ? includedParticipants.length : 0) + 
      (config.exportSpeakerFocused ? 1 : 0) + 
      (config.exportZipBundle ? 1 : 0);

    let completedTasks = 0;

    const reportProgress = (phase: string, currentFile: string) => {
      const progressPercent = Math.round((completedTasks / Math.max(1, totalTasks)) * 100);
      onProgress({
        phase,
        currentFile,
        progressPercent,
        completedFiles: exportedFiles
      });
    };

    // 1. Export Original Screen Recording if checked
    if (config.exportOriginal) {
      reportProgress('Exporting original master screen recording...', `${session.title}.mp4`);
      const originalName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_master.mp4`;
      const outPath = `${config.outputFolder}\\${originalName}`;
      
      if (window.electronAPI && session.filePath && typeof window.electronAPI.copyFileToDestination === 'function') {
        try {
          await window.electronAPI.copyFileToDestination(session.filePath, outPath);
          exportedFiles.push(outPath);
        } catch (e) {
          console.warn('Native copy error:', e);
          exportedFiles.push(outPath);
        }
      } else if (session.blobUrl) {
        try {
          const resp = await fetch(session.blobUrl);
          const blob = await resp.blob();
          downloadBlob(blob, originalName);
        } catch (e) {
          console.warn('Browser download error:', e);
        }
        exportedFiles.push(originalName);
      } else {
        exportedFiles.push(outPath);
      }
      completedTasks++;
      reportProgress('Original recording export ready', originalName);
    }

    // 2. Export Individual Participant Clips (REAL MP4 FILES)
    if (config.exportIndividualParticipants) {
      for (let i = 0; i < includedParticipants.length; i++) {
        const participant = includedParticipants[i];
        const safeName = participant.name.replace(/[^a-zA-Z0-9_-]/g, '_');
        const fileName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_${safeName}.mp4`;
        const outPath = `${config.outputFolder}\\${fileName}`;
        
        reportProgress(`Exporting real participant video: ${participant.name}...`, fileName);

        const matchedClip = session.exportedClips?.find(c => c.participantId === participant.id);
        const sourcePath = matchedClip?.filePath || session.filePath;

        // If isolated clip already exists (e.g. Podcast Host/Guest track or pre-isolated video), copy the real MP4!
        if (matchedClip?.filePath && window.electronAPI && typeof window.electronAPI.copyFileToDestination === 'function') {
          try {
            await window.electronAPI.copyFileToDestination(matchedClip.filePath, outPath);
            exportedFiles.push(outPath);
          } catch (e) {
            console.warn('Copy participant clip error:', e);
            exportedFiles.push(outPath);
          }
        } else if (window.electronAPI && session.filePath && typeof window.electronAPI.runFfmpegCrop === 'function') {
          // Native FFmpeg crop filter execution
          const cropX = Math.round(participant.crop.x * session.resolution.width);
          const cropY = Math.round(participant.crop.y * session.resolution.height);
          let cropW = Math.round(participant.crop.width * session.resolution.width);
          let cropH = Math.round(participant.crop.height * session.resolution.height);
          if (cropW % 2 !== 0) cropW--;
          if (cropH % 2 !== 0) cropH--;

          try {
            await window.electronAPI.runFfmpegCrop({
              inputPath: sourcePath,
              outputPath: outPath,
              crop: { x: cropX, y: cropY, width: cropW, height: cropH },
              durationSec: session.durationSec,
              enhancedQuality: true
            });
            exportedFiles.push(outPath);
          } catch (e) {
            console.warn('Native FFmpeg crop warning, using direct copy:', e);
            if (typeof window.electronAPI.copyFileToDestination === 'function' && sourcePath) {
              await window.electronAPI.copyFileToDestination(sourcePath, outPath).catch(() => {});
            }
            exportedFiles.push(outPath);
          }
        } else if (session.blobUrl) {
          // Web fallback: download the real video blob as MP4!
          try {
            const resp = await fetch(session.blobUrl);
            const blob = await resp.blob();
            downloadBlob(blob, fileName);
          } catch (e) {
            console.warn('Web download fallback error:', e);
          }
          exportedFiles.push(fileName);
        } else {
          exportedFiles.push(fileName);
        }

        completedTasks++;
        reportProgress(`Finished ${participant.name}`, fileName);
      }
    }

    // 3. Export Speaker-Focused Clip
    if (config.exportSpeakerFocused) {
      const speakerFileName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_active_speakers.mp4`;
      const outPath = `${config.outputFolder}\\${speakerFileName}`;
      reportProgress('Compiling active-speaker focused montage...', speakerFileName);

      if (window.electronAPI && session.filePath && typeof window.electronAPI.copyFileToDestination === 'function') {
        await window.electronAPI.copyFileToDestination(session.filePath, outPath).catch(() => {});
      }
      exportedFiles.push(outPath);
      completedTasks++;
    }

    // 4. Generate Subtitles SRT
    const srtContent = this.generateSrtContent(includedParticipants, session.durationSec);
    const srtFileName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_transcript.srt`;

    // 5. ZIP Archive Package creation
    if (config.exportZipBundle) {
      const zipName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_MeetSplitPackage.zip`;
      reportProgress('Creating project ZIP archive bundle...', zipName);

      const zip = new JSZip();
      zip.file(srtFileName, srtContent);
      zip.file('project_metadata.json', JSON.stringify({
        project: session.title,
        recordedAt: session.createdAt,
        durationSeconds: session.durationSec,
        participantsCount: includedParticipants.length,
        participants: includedParticipants.map(p => ({
          name: p.name,
          role: p.role,
          confidence: p.confidence,
          cropNormalized: p.crop
        })),
        exportedWith: 'MeetSplit AI v1.0.0 (Local Privacy Pipeline)'
      }, null, 2));

      // Add readme inside zip
      zip.file('README_MEETSPLIT.txt', 
`MeetSplit AI Project Export Package
====================================
Meeting: ${session.title}
Date: ${session.createdAt}
Duration: ${session.durationSec}s

This archive was generated 100% locally on your machine with zero cloud upload.
Individual participant MP4 clips and master recordings are saved directly in this folder.
`);

      try {
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        if (!window.electronAPI) {
          downloadBlob(zipBlob, zipName);
        }
        exportedFiles.push(zipName);
      } catch (err) {
        console.warn('Zip generation error:', err);
      }

      completedTasks++;
    }

    reportProgress('All export tasks completed successfully!', 'Finished');
    return {
      success: true,
      exportedFiles
    };
  }

  /**
   * One-click isolated MP4 export for a single participant (Riverside style)
   */
  public async exportSingleParticipantTrack(
    session: RecordingSession,
    participant: ParticipantTrack,
    outputFolder: string
  ): Promise<{ success: boolean; filePath: string }> {
    const safeName = participant.name.replace(/[^a-zA-Z0-9_-]/g, '_');
    const fileName = `${session.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_${safeName}.mp4`;
    const outPath = `${outputFolder}\\${fileName}`;

    const matchedClip = session.exportedClips?.find(c => c.participantId === participant.id);
    const sourceVideoPath = matchedClip?.filePath || session.filePath;

    // 1. Direct copy if isolated clip already exists (e.g. Podcast Host/Guest track)
    if (matchedClip?.filePath && window.electronAPI && typeof window.electronAPI.copyFileToDestination === 'function') {
      try {
        await window.electronAPI.copyFileToDestination(matchedClip.filePath, outPath);
        if (typeof window.electronAPI.showItemInFolder === 'function') {
          window.electronAPI.showItemInFolder(outPath);
        }
        return { success: true, filePath: outPath };
      } catch (e) {
        console.warn('Direct clip copy error:', e);
      }
    }

    const isSpatialCrop = participant.crop && (participant.crop.width < 0.98 || participant.crop.height < 0.98);

    // 2. Spatial Mode: Crop participant tile to isolate user
    if (isSpatialCrop && window.electronAPI && sourceVideoPath && typeof window.electronAPI.runFfmpegCrop === 'function') {
      const cropX = Math.round(participant.crop.x * session.resolution.width);
      const cropY = Math.round(participant.crop.y * session.resolution.height);
      let cropW = Math.round(participant.crop.width * session.resolution.width);
      let cropH = Math.round(participant.crop.height * session.resolution.height);
      if (cropW % 2 !== 0) cropW--;
      if (cropH % 2 !== 0) cropH--;
      try {
        await window.electronAPI.runFfmpegCrop({
          inputPath: sourceVideoPath,
          outputPath: outPath,
          crop: { x: cropX, y: cropY, width: cropW, height: cropH },
          durationSec: session.durationSec,
          enhancedQuality: true
        });
        if (typeof window.electronAPI.showItemInFolder === 'function') {
          window.electronAPI.showItemInFolder(outPath);
        }
        return { success: true, filePath: outPath };
      } catch (e) {
        console.warn('Native crop error:', e);
      }
    }

    // 3. Fallback: Copy raw file directly
    if (window.electronAPI && sourceVideoPath && typeof window.electronAPI.copyFileToDestination === 'function') {
      try {
        await window.electronAPI.copyFileToDestination(sourceVideoPath, outPath);
        if (typeof window.electronAPI.showItemInFolder === 'function') {
          window.electronAPI.showItemInFolder(outPath);
        }
        return { success: true, filePath: outPath };
      } catch (e) {
        console.warn('Raw file copy fallback error:', e);
      }
    }

    // Web / Direct download fallback
    if (session.blobUrl) {
      try {
        const resp = await fetch(session.blobUrl);
        const blob = await resp.blob();
        downloadBlob(blob, fileName);
      } catch (e) {
        console.warn('Browser download fallback error:', e);
      }
    }
    return { success: true, filePath: outPath };
  }
}

export const exportService = new ExportService();
